import React, { useState } from 'react';
import LinkedInShare from './LinkedInShare';
import { publicProfileUrl } from '../utils/profileSharing';

const G = '#004D35';
const BORDER = '#e8e5e0';
const DARK = '#0a1a0f';

const qrUrl = (url) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;

const TABS = [
  { key: 'linkedin', label: 'LinkedIn post' },
  { key: 'signature', label: 'Email signature' },
  { key: 'email', label: 'Intro email' },
  { key: 'qr', label: 'QR code' },
];

function CopyButton({ text, channel, copied, onCopy, label = 'Copy' }) {
  const done = copied === channel;
  return (
    <button
      onClick={() => onCopy(text, channel)}
      style={{
        padding: '9px 22px',
        background: done ? '#009966' : G,
        color: '#fff',
        border: 'none',
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        fontFamily: 'sans-serif',
        transition: 'background 0.2s',
        minWidth: 90,
      }}
    >
      {done ? '✓ Copied!' : label}
    </button>
  );
}

function ShareKit({ advisorSlug, advisorName, advisorTitle, getAuthHeader }) {
  const [tab, setTab] = useState('linkedin');
  const [copied, setCopied] = useState(null);

  const profileUrl = publicProfileUrl(advisorSlug);

  const logShare = async (channel) => {
    if (!getAuthHeader) return;
    try {
      await fetch('/api/advisor/me/share-event', {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel }),
      });
    } catch (_) {}
  };

  const handleCopy = async (text, channel) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(channel);
      logShare(channel);
      setTimeout(() => setCopied(null), 2200);
    } catch (_) {}
  };

  const name = advisorName || 'Your Name';
  const title = advisorTitle || 'Business Advisor';

  const linkedinText =
    `I just joined Viysr — a platform connecting small business owners with professional advisors.\n\n` +
    `If you own a business and need guidance on ${title.toLowerCase()}, I'd love to connect!\n\n` +
    `View my profile and get in touch:\n${profileUrl}\n\n` +
    `#SmallBusiness #Advisor #Viysr`;

  const signatureHtml =
    `<table style="font-family:sans-serif;font-size:13px;color:#333;border-top:2px solid #004D35;padding-top:10px;margin-top:10px;border-collapse:collapse">` +
    `<tr><td style="font-weight:700;font-size:15px;color:#0a1a0f;padding-bottom:2px">${name}</td></tr>` +
    `<tr><td style="color:#555;font-size:12px;padding-bottom:6px">${title}</td></tr>` +
    `<tr><td><a href="${profileUrl}" style="color:#004D35;text-decoration:none;font-size:12px">View my Viysr profile</a></td></tr>` +
    `</table>`;

  const introEmail =
    `Subject: Business advisory — ${name}, ${title}\n\n` +
    `Hi [First Name],\n\n` +
    `I came across your business and thought I might be able to help with [specific challenge].\n\n` +
    `I'm a ${title} and I work with small business owners on [your key specialty].\n\n` +
    `You can learn more about my work and get in touch here:\n${profileUrl}\n\n` +
    `Looking forward to connecting,\n\n` +
    `${name}`;

  return (
    <div>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '7px 16px',
              borderRadius: 999,
              border: `1.5px solid ${tab === t.key ? G : BORDER}`,
              background: tab === t.key ? G : '#fff',
              color: tab === t.key ? '#fff' : '#555',
              fontSize: 13,
              fontWeight: tab === t.key ? 700 : 400,
              cursor: 'pointer',
              fontFamily: 'sans-serif',
              transition: 'all 0.15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'linkedin' && (
        <LinkedInShare profileUrl={profileUrl} postText={linkedinText} defaultOpen onCopy={() => logShare('linkedin')} />
      )}

      {tab === 'signature' && (
        <div>
          <p style={{ fontSize: 12, color: '#aaa', fontFamily: 'sans-serif', margin: '0 0 10px' }}>
            Paste into your email client's signature settings (HTML mode).
          </p>
          <div style={{
            background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 12,
            padding: '16px 18px', marginBottom: 10,
          }}>
            <div dangerouslySetInnerHTML={{ __html: signatureHtml }} />
          </div>
          <div style={{ fontSize: 11, color: '#bbb', fontFamily: 'sans-serif', marginBottom: 12 }}>
            The button below copies the HTML source to your clipboard — paste it into your email app's HTML signature editor.
          </div>
          <CopyButton text={signatureHtml} channel="signature" copied={copied} onCopy={handleCopy} label="Copy HTML" />
        </div>
      )}

      {tab === 'email' && (
        <div>
          <p style={{ fontSize: 12, color: '#aaa', fontFamily: 'sans-serif', margin: '0 0 10px' }}>
            A warm outreach template — personalise the bracketed parts before sending.
          </p>
          <div style={{
            background: '#f9f9f7', border: `1.5px solid ${BORDER}`, borderRadius: 12,
            padding: '16px 18px', fontSize: 13.5, fontFamily: 'sans-serif', color: DARK,
            lineHeight: 1.75, whiteSpace: 'pre-wrap', marginBottom: 14,
          }}>
            {introEmail}
          </div>
          <CopyButton text={introEmail} channel="email" copied={copied} onCopy={handleCopy} label="Copy email" />
        </div>
      )}

      {tab === 'qr' && (
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 12, color: '#aaa', fontFamily: 'sans-serif', margin: '0 0 16px' }}>
            Print this on business cards, brochures, or flyers to send people straight to your profile.
          </p>
          <div style={{
            display: 'inline-block', padding: 16, background: '#fff',
            border: `1.5px solid ${BORDER}`, borderRadius: 12, marginBottom: 16,
          }}>
            <img
              src={qrUrl(profileUrl)}
              alt="QR code for your Viysr profile"
              width={180}
              height={180}
              style={{ display: 'block' }}
            />
          </div>
          <div style={{ fontSize: 12, color: '#aaa', fontFamily: 'sans-serif', marginBottom: 14 }}>
            {profileUrl}
          </div>
          <CopyButton text={profileUrl} channel="qr" copied={copied} onCopy={handleCopy} label="Copy link" />
        </div>
      )}
    </div>
  );
}

export default ShareKit;
